import { auth } from '../firebase';
import { getWalletMe, walletOperation } from './secureFunctions';

export interface AdvertiserWallet {
  balanceUSD: number;
  reservedUSD: number;
  updatedAt?: any;
}

const toMinorUnits = (amountUsd: number) => {
  const normalized = Number(amountUsd) || 0;
  const cents = Math.round(normalized * 100);
  if (cents <= 0) {
    throw new Error('amount-positive-required');
  }
  return cents;
};

const toUsd = (minor?: number) => {
  if (typeof minor !== 'number' || Number.isNaN(minor)) {
    return 0;
  }
  return Number((minor / 100).toFixed(2));
};

export const getWallet = async (uid?: string): Promise<AdvertiserWallet | null> => {
  const userId = uid || auth.currentUser?.uid;
  if (!userId) return null;

  // This helper is used for the currently authenticated advertiser.
  // For now we only support self reads via the VPS API.
  if (!auth.currentUser || auth.currentUser.uid !== userId) {
    return null;
  }

  const me = await getWalletMe();
  const currency = String(me.account.currency || 'USD').toUpperCase();
  const balanceMinor = Number.parseInt(String(me.account.balanceMinor || '0'), 10);

  // Wallet ledger is stored as minor units (USD cents).
  // If a different currency is used, we still return 0 here to avoid misleading UI.
  if (currency !== 'USD') {
    return { balanceUSD: 0, reservedUSD: 0, updatedAt: me.account.updatedAt };
  }

  return {
    balanceUSD: toUsd(balanceMinor),
    reservedUSD: 0,
    updatedAt: me.account.updatedAt,
  };
};

export const creditWalletServer = async (amountUSD: number, reference?: string, provider?: string, targetUid?: string) => {
  if (!auth.currentUser) {
    throw new Error('auth-required');
  }
  const payload = await walletOperation({
    op: 'credit',
    amount: toMinorUnits(amountUSD),
    currency: 'USD',
    targetUserId: targetUid || auth.currentUser.uid,
    memo: reference ? reference.slice(0, 120) : 'wallet_topup',
    metadata: {
      provider: provider || 'unknown',
      reference: reference || null,
      source: 'client-topup',
    },
  });
  return payload.ok === true;
};

export const deductWalletServer = async (
  advertiserId: string,
  adId: string,
  amountUSD: number,
  type: 'impression' | 'click',
  placement: string
): Promise<{ success: boolean; reason?: string; newBalance?: number; adPaused?: boolean }> => {
  if (!auth.currentUser || auth.currentUser.uid !== advertiserId) {
    console.warn('Wallet deductions require the advertiser to be authenticated.');
    return { success: false, reason: 'NOT_ADVERTISER' };
  }
  try {
    const response = await walletOperation({
      op: 'debit',
      amount: toMinorUnits(amountUSD),
      currency: 'USD',
      memo: `ad:${adId}:${type}`.slice(0, 120),
      metadata: {
        advertiserId,
        adId,
        type,
        placement,
      },
    });
    return {
      success: response.ok,
      newBalance: toUsd((response.result as any)?.balance),
      adPaused: response.ok ? false : true,
    };
  } catch (error: any) {
    return { success: false, reason: error?.message || 'UNKNOWN_ERROR' };
  }
};
